/**
 * Onde a bolinha do Lucas fica, e se ela aparece.
 *
 * Mora no `localStorage` e não no banco de propósito: posição é preferência de
 * APARELHO, não de conta. A mesma pessoa num monitor ultrawide e num notebook
 * quer a bolinha em lugares diferentes, e sincronizar isso pelo servidor só
 * produziria uma posição errada em metade das telas. É a mesma escolha que o
 * tema, a paleta de nós do flow e o aside do CRM já fazem.
 *
 * A chave inclui o usuário porque "esconder" é decisão de pessoa: numa máquina
 * compartilhada, quem escondeu não deve esconder para o colega.
 *
 * ## Por que uma loja externa, e não `useState`
 *
 * A preferência é lida em dois lugares ao mesmo tempo: a bolinha, flutuando
 * sobre a plataforma, e a tela de configurações, que a edita. Com estado local
 * por instância, desligar o atalho nas configurações não moveria a bolinha que
 * já está montada: só valeria no próximo carregamento. Uma loja de módulo com
 * `useSyncExternalStore` mantém as duas em sincronia sem precisar de um
 * provider só para isto.
 */

import { useCallback, useSyncExternalStore } from "react";

const PREFIX = "tw:lucasWidget";

/** Distância padrão das bordas, em px. Canto inferior direito. */
export const DEFAULT_POSITION = { right: 24, bottom: 24 };

/** Metade do diâmetro da bolinha, usada para não deixá-la sair da tela. */
const EDGE_MARGIN = 8;
const BUBBLE_SIZE = 56;

const keyFor = (userId) => `${PREFIX}:${userId || "anon"}`;

function lerDoDisco(userId) {
  try {
    const raw = localStorage.getItem(keyFor(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    // Janela anônima, armazenamento bloqueado ou JSON corrompido: a bolinha
    // volta ao padrão em vez de derrubar o layout inteiro.
    return null;
  }
}

function gravarNoDisco(userId, value) {
  try {
    localStorage.setItem(keyFor(userId), JSON.stringify(value));
  } catch {
    // Não poder lembrar a posição não é motivo para impedir o arrasto.
  }
}

function normalizar(bruto) {
  return {
    position: bruto?.position ?? DEFAULT_POSITION,
    hidden: bruto?.hidden === true,
    // Some depois do primeiro uso: a pulsada é para quem ainda não sabe que o
    // Lucas existe, não um lembrete perpétuo para quem já decidiu.
    discovered: bruto?.discovered === true,
    /**
     * Quando a pulsada apareceu pela última vez, em epoch.
     *
     * Fica no `localStorage`, e não no `sessionStorage`, por duas razões que
     * apareceram ao testar: a trava por aba multiplicava o chamariz para quem
     * trabalha com várias abas, e sumia de vez para quem deixa a plataforma
     * aberta a semana inteira, que é justamente o perfil que a gente quer
     * alcançar. Tempo é independente das duas coisas.
     */
    lastNudgeAt: Number.isFinite(bruto?.lastNudgeAt) ? bruto.lastNudgeAt : 0,
  };
}

// ── Loja de módulo ──────────────────────────────────────────────────────────
// O cache guarda a MESMA referência entre leituras: `useSyncExternalStore`
// exige isso, senão o React re-renderiza em laço.
const ouvintes = new Set();
const cache = new Map();

function instantaneo(userId) {
  if (!cache.has(userId)) cache.set(userId, normalizar(lerDoDisco(userId)));
  return cache.get(userId);
}

function atualizar(userId, patch) {
  const atual = instantaneo(userId);
  const proximo = { ...atual, ...patch };
  cache.set(userId, proximo);
  gravarNoDisco(userId, proximo);
  ouvintes.forEach((ouvinte) => ouvinte());
}

function assinar(ouvinte) {
  ouvintes.add(ouvinte);
  return () => ouvintes.delete(ouvinte);
}

/**
 * Esquece o que está em memória e relê do disco na próxima leitura.
 *
 * Serve ao logout (a próxima pessoa na mesma aba não herda a preferência de
 * quem saiu) e aos testes, que limpam o `localStorage` entre cenários.
 */
export function limparCacheDePreferencias() {
  cache.clear();
  ouvintes.forEach((ouvinte) => ouvinte());
}

/**
 * Mantém a bolinha dentro da janela.
 *
 * Uma posição guardada num monitor grande deixaria a bolinha fora da tela num
 * notebook, e sem isso ela ficaria inalcançável sem limpar o armazenamento.
 */
export function clampPosition(position, viewport) {
  const maxRight = Math.max(
    EDGE_MARGIN,
    (viewport?.width ?? 0) - BUBBLE_SIZE - EDGE_MARGIN,
  );
  const maxBottom = Math.max(
    EDGE_MARGIN,
    (viewport?.height ?? 0) - BUBBLE_SIZE - EDGE_MARGIN,
  );
  return {
    right: Math.min(Math.max(position.right, EDGE_MARGIN), maxRight),
    bottom: Math.min(Math.max(position.bottom, EDGE_MARGIN), maxBottom),
  };
}

/**
 * O id que dá nome à chave de armazenamento.
 *
 * Vive aqui, e não em cada chamador, porque a bolinha e a tela de preferências
 * precisam chegar à MESMA chave: se uma usasse `id` e a outra `userId`, ligar o
 * atalho nas configurações gravaria num lugar que a bolinha nunca lê.
 *
 * O `/auth/me` devolve `userId` (e `localUserId` no shell embutido); `id` fica
 * como último recurso para chamadores que já tenham o objeto de outra fonte.
 */
export function idDoUsuario(user) {
  return user?.userId || user?.localUserId || user?.id;
}

/** `true` quando a bolinha foi arrastada para longe do canto padrão. */
export function foiMovida(position) {
  return (
    position.right !== DEFAULT_POSITION.right ||
    position.bottom !== DEFAULT_POSITION.bottom
  );
}

export default function useLucasWidgetPrefs(userId) {
  const prefs = useSyncExternalStore(
    assinar,
    useCallback(() => instantaneo(userId), [userId]),
  );

  const setPosition = useCallback(
    (position) => atualizar(userId, { position }),
    [userId],
  );

  const setHidden = useCallback(
    (hidden) => atualizar(userId, { hidden }),
    [userId],
  );

  const resetPosition = useCallback(
    () => atualizar(userId, { position: DEFAULT_POSITION }),
    [userId],
  );

  const markDiscovered = useCallback(() => {
    // Evita uma escrita por clique depois do primeiro.
    if (instantaneo(userId).discovered) return;
    atualizar(userId, { discovered: true });
  }, [userId]);

  /** Registra que a pulsada apareceu agora, iniciando a janela de descanso. */
  const markNudged = useCallback(
    () => atualizar(userId, { lastNudgeAt: Date.now() }),
    [userId],
  );

  return {
    ...prefs,
    setPosition,
    setHidden,
    resetPosition,
    markDiscovered,
    markNudged,
  };
}
